import { useState } from 'react';
import { connect } from 'react-redux';
import { 
  selectTripadvisorResults,
  fetchLocationHighlights
} from '../redux/tripadvisorSlice';
import TripadvisorPreviewModal from './TripadvisorPreviewModal';
import { getBusinessInitials } from '../utils/utils';

const TripadvisorResultsList = ({ 
  searchResults, 
  onSelect, 
  selectedResult, 
  dispatch,
  onChange,
  onFieldError,
  forceRecordUpdate,
  onBlur,
  record,
  savedDisplayData,
  isEditMode,
  showPreview = true,
  stickyHeader = true
}) => {
  const [, setHoveredResultId] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  
  const isDevelopment = window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1';

  const decodeHtmlEntities = (text) => {
    if (!text) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    let decoded = textarea.value;
    
    // Remove ALL ">" characters completely from the text
    decoded = decoded.replace(/>/g, '');
    
    return decoded;
  };

  const handlePreviewClick = (locationId) => {
    dispatch(fetchLocationHighlights(locationId));
    setPreviewModalOpen(true);
  };

  const renderResultItem = (result, isSelected) => {
    return (
      <div key={result.id} 
        className="tripadvisor-result-item" 
        style={{ 
          padding: '15px', 
          border: isSelected ? '2px solid var(--dark-green-color)' : '1px solid #ddd',
          borderRadius: '4px',
          cursor: isEditMode ? 'default' : 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          position: 'relative',
          backgroundColor: isSelected ? '#e1effa' : '#fff',
          transition: 'all 0.2s ease',
          opacity: isEditMode && !isSelected ? 0.6 : 1
        }}
        onClick={(e) => {
          e.preventDefault();
          
          if (isEditMode) return;
          
          const selected = {
            id: result.id,
            name: result.name,
            location: result.location
          };
          
          handleTripadvisorResultSelect(selected, result);
        }}
        onMouseEnter={() => !isEditMode && setHoveredResultId(result.id)}
        onMouseLeave={() => !isEditMode && setHoveredResultId(null)}
      >
        
        {}
        <div className="tripadvisor-result-header">
          {}
          <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '4px' }}>
            {decodeHtmlEntities(result.name)}
          </div>
          
          {}
          <div style={{ fontSize: '15px', color: '#444', marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
            {result.city && (
              <span><strong>City:</strong> {decodeHtmlEntities(result.city)}</span>
            )}
            {result.country && (
              <span><strong>Country:</strong> {decodeHtmlEntities(result.country)}</span>
            )}
          </div>
          
          {}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
            {result.rating && (
              <span style={{ 
                background: '#00aa6c', 
                color: 'white', 
                padding: '2px 8px', 
                borderRadius: '3px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                {result.rating}
              </span>
            )}
            {result.numReviews && (
              <span style={{ color: '#666', fontSize: '14px' }}>
                ({result.numReviews} reviews)
              </span>
            )}
            {result.priceLevel && (
              <span style={{ 
                color: '#444', 
                fontSize: '14px',
                fontWeight: 'bold',
                marginLeft: 'auto'  
              }}>
                {result.priceLevel}
              </span>
            )}
          </div>
          
          {}
          {result.ranking && (
            <div style={{ fontSize: '14px', color: '#444', marginBottom: '5px' }}>
              {decodeHtmlEntities(result.ranking)}
            </div>
          )}
        </div>
        
        {}
        {result.primaryPhoto ? (
          <div className="tripadvisor-result-photo" style={{ 
            backgroundImage: `url(${result.primaryPhoto})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: '3px',
            width: '100%',
            height: '180px'
          }} />
        ) : getBusinessInitials(result.name) ? (
          <div className="tripadvisor-result-initials" style={{
            fontSize: '34px',
            fontWeight: 600,
            letterSpacing: '2px',
            color: '#5a6b7f',
            backgroundColor: '#eef1f5',
            borderRadius: '3px',
            width: '100%',
            height: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {getBusinessInitials(result.name)}
          </div>
        ) : null}
        
        {}
        {result.description && (
          <div style={{ 
            fontSize: '14px', 
            color: '#555', 
            lineHeight: '1.4',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical'
          }}>
            {decodeHtmlEntities(result.description)}
          </div>
        )}
        
        {}
        <div className="tripadvisor-details" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {}
          {result.location && (
            <div style={{ fontSize: '14px', color: '#555' }}>
              <strong>Address:</strong> {decodeHtmlEntities(result.location)}
            </div>
          )}
          
          {}
          <div style={{ 
            fontSize: '13px', 
            color: '#777',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            {result.categoryLocalizedName && <span><strong>Type:</strong> {decodeHtmlEntities(result.categoryLocalizedName)}</span>}
          </div>
          
          {}
          <div style={{ 
            marginTop: '10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            {}
            <div>
              {result.webUrl && (
                <a 
                  href={result.webUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()} // Prevent triggering the parent onClick
                  style={{ color: '#0a8d87', fontSize: '13px' }}
                >
                  View on Tripadvisor
                </a>
              )}
            </div>
            
            {}
            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              {isEditMode ? (
                isSelected && (
                  <div style={{
                    background: 'var(--primary-color)',
                    color: 'white',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }}>
                    ✓
                  </div>
                )
              ) : isSelected ? (
                <>
                  {}
                  {isDevelopment && showPreview && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreviewClick(result.id);
                      }}
                      className="button secondary"
                      style={{
                        padding: '5px 10px',
                        fontSize: '12px'
                      }}
                      title="Preview the content that will be saved (Development only)"
                    >
                      Preview
                    </button>
                  )}
                  
                  {}
                  <div style={{
                    background: 'var(--primary-color)',
                    color: 'white',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }}>
                    ✓
                  </div>
                </>
              ) : (
                <>
                  {}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTripadvisorResultSelect({
                        id: result.id,
                        name: result.name,
                        location: result.location
                      }, result);
                    }}
                    className="button plain"
                    style={{
                      padding: '5px 10px',
                      fontSize: '14px'
                    }}
                  >
                    Select
                  </button>
                  
                  {}
                  {isDevelopment && (
                    <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        const id = result.id;
                        
                        const customEvent = new CustomEvent("tripadvisorLocationSelected", {
                          detail: { locationId: id }
                        });
                        document.dispatchEvent(customEvent);
                        
                        handleTripadvisorResultSelect({
                          id: id,
                          name: result.name,
                          location: result.location
                        }, result);
                      }}
                      style={{
                        background: '#f5f5f5',
                        color: '#555',
                        border: '1px solid #ddd',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                      title="Auto-select this result"
                    >
                      ✓
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleTripadvisorResultSelect = (result, fullResultData = null) => {
    if (result && result.id && onChange) {
      
      try {
        if (typeof result.id !== 'string' && typeof result.id !== 'number') {
          return;
        }
        
        // Clear any validation errors BEFORE updating the location
        if (onFieldError) {
          onFieldError('locationId', null);
        }
        
        onChange(result.id, 'locationId');
        
        // Backup method: Force update using a direct call to InputForm's setState if available
        if (forceRecordUpdate) {
          forceRecordUpdate('locationId', result.id);
        }
        
        // Store the hotel name in a separate field (not source)
        if (result.name) {
          onChange(result.name, 'tripadvisorLocationName');
          if (forceRecordUpdate) {
            forceRecordUpdate('tripadvisorLocationName', result.name);
          }
        }
        
        const currentCategory = record?.tripadvisorCategory;
        
        if (!currentCategory) {
          onChange('geos', 'tripadvisorCategory');
        } else {
          // Explicitly re-set the category to ensure it stays selected
          // This ensures radio buttons remain selected after result selection
          onChange(currentCategory, 'tripadvisorCategory');
        }
        
        if (fullResultData && forceRecordUpdate) {
          const displayData = {
            name: decodeHtmlEntities(fullResultData.name),
            rating: fullResultData.rating,
            numReviews: fullResultData.numReviews,
            priceLevel: fullResultData.priceLevel,
            primaryPhoto: fullResultData.primaryPhoto,
            description: decodeHtmlEntities(fullResultData.description),
            location: decodeHtmlEntities(fullResultData.location),
            city: decodeHtmlEntities(fullResultData.city),
            country: decodeHtmlEntities(fullResultData.country),
            ranking: decodeHtmlEntities(fullResultData.ranking),
            categoryLocalizedName: decodeHtmlEntities(fullResultData.categoryLocalizedName),
            webUrl: fullResultData.webUrl
          };
          
          forceRecordUpdate('tripadvisorDisplayData', displayData);
        }
        
        // Give time for state updates to complete, then trigger validation
        setTimeout(() => {
          if (onBlur) {
            onBlur('locationId', result.id);
          }
        }, 150);
      } catch {
        if (window.location.hostname !== 'localhost') {
          console.error('Error during TripAdvisor selection');
        }
      }
    }

    if (onSelect) {
      onSelect(result);
    }
  };

  if (!searchResults || searchResults.length === 0) {
    if (savedDisplayData && selectedResult) {
      const savedResult = {
        ...savedDisplayData,
        id: selectedResult.id
      };
      
      return (
        <div className="tripadvisor-results" style={{ 
          marginTop: '15px', 
          height: 'auto',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            padding: '0 5px'
          }}>
            <h4 style={{ margin: 0 }}>
              Saved Location
            </h4>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gap: '10px'
          }}>
            {renderResultItem(savedResult, true)}
          </div>
        </div>
      );
    }
    
    if (!selectedResult) {
      return (
        <div style={{
          padding: '15px',
          marginTop: '15px',
          backgroundColor: '#f9f9f9',
          border: '1px dashed #ccc',
          borderRadius: '4px',
          textAlign: 'center',
          color: '#666'
        }}>
          <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>
            Location Selection Required
          </div>
          <p style={{ fontSize: '14px' }}>
            Please search and select a TripAdvisor location to continue.
            <br />
            This is required to properly index content for knowledge base search.
          </p>
        </div>
      );
    }
    return null;
  }

  const resultsToDisplay = [...searchResults];
  
  if (savedDisplayData && selectedResult && !searchResults.find(r => r.id === selectedResult.id)) {
    const savedResultToShow = {
      ...savedDisplayData,
      id: selectedResult.id
    };
    resultsToDisplay.unshift(savedResultToShow);
  }

  return (
    <div className="tripadvisor-results" style={{ 
      marginTop: '15px', 
      height: '600px',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {}
      <TripadvisorPreviewModal 
        isOpen={previewModalOpen} 
        onClose={() => setPreviewModalOpen(false)} 
      />
      
      {}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
        padding: '0 5px',
        position: stickyHeader ? 'sticky' : 'static',
        top: 0,
        background: 'white',
        zIndex: 10
      }}>
        <h4 style={{ margin: 0 }}>
          {isEditMode ? 'Saved Location' : (savedDisplayData && !searchResults.length ? 'Saved Location' : `Search Results (${searchResults.length})`)}
        </h4>
      </div>
      
      {}
      <div style={{ 
        display: 'grid', 
        gap: '10px', 
        overflowY: 'auto',
        flex: 1,
        paddingRight: '5px',
        height: '540px' /* Fixed height accounting for header (60px) */
      }}>
        {resultsToDisplay.map(result => {
          const isSelected = selectedResult && selectedResult.id === result.id;
          
          return renderResultItem(result, isSelected);
        })}
      </div>
      
      {}
      <div style={{ marginTop: '10px' }}></div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  searchResults: selectTripadvisorResults(state)
});

export default connect(mapStateToProps)(TripadvisorResultsList);